import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { COMPANY } from "@/config/company";
import NotFound from "@/pages/NotFound";

// Last reviewed — update when policy text changes.
const UPDATED = "29 August 2026";

const addr = COMPANY.registeredOffice;
const go = COMPANY.grievanceOfficer;

// Each policy is a list of { h, p } sections. `p` may be a string, an array of
// strings (paragraphs), or JSX. Content is tailored to this marketplace and to
// Indian law (Companies Act 2013, Consumer Protection (E-Commerce) Rules 2020,
// IT Rules 2021, GST, DPDP Act 2023).
const IDENTITY = (
  <div className="not-prose border border-foreground p-6 mb-2 font-mono text-[13px] leading-relaxed">
    <p><span className="text-muted-foreground">Legal entity:</span> {COMPANY.legalName}</p>
    <p><span className="text-muted-foreground">Brand:</span> {COMPANY.brand} (thespotters.in)</p>
    <p><span className="text-muted-foreground">CIN:</span> {COMPANY.cin}</p>
    <p><span className="text-muted-foreground">GSTIN:</span> {COMPANY.gstin}</p>
    <p><span className="text-muted-foreground">Registered office:</span> {addr}</p>
    <p><span className="text-muted-foreground">Email:</span> {COMPANY.email} · <span className="text-muted-foreground">Phone:</span> {COMPANY.phone}</p>
  </div>
);

const POLICIES = {
  terms: {
    eyebrow: "Legal",
    title: "Terms of Use.",
    intro: `These Terms govern your use of ${COMPANY.brand} (thespotters.in), a marketplace operated by ${COMPANY.legalName}. By accessing or using the platform, you agree to these Terms.`,
    sections: [
      { h: "1. Who we are", p: <>{IDENTITY}<p>{COMPANY.brand} is a brand owned and operated by {COMPANY.legalName}, a company incorporated in India under the Companies Act, 2013 (“the Company”, “we”, “us”).</p></> },
      { h: "2. The marketplace", p: [
        `${COMPANY.brand} is an intermediary that connects independent sellers (“Vendors”) with buyers. Products are sold by Vendors; the Company facilitates listing, ordering, payment and delivery.`,
        "Every product is physically inspected at the Spotters Hub against a reference sample before dispatch. Certification confirms our inspection was performed; it is not a manufacturer warranty.",
      ]},
      { h: "3. Eligibility & account", p: [
        "You must be at least 18 years old and capable of entering a binding contract under Indian law.",
        "You are responsible for the accuracy of the information in your account and for activity under your login. Keep your credentials confidential.",
      ]},
      { h: "4. Orders, pricing & payment", p: [
        "All prices are listed in Indian Rupees (INR) and are inclusive of applicable GST (currently 18%). Shipping is free on orders of ₹2,000 and above; a flat ₹99 applies below that.",
        "Payments are processed by our payment partner, Razorpay. We do not store your card, UPI or bank credentials. An order is confirmed only after payment is successfully verified.",
        "We may cancel an order in cases of suspected fraud, pricing error, or stock unavailability, with a full refund of any amount paid.",
      ]},
      { h: "5. Shipping, returns & refunds", p: <>See our <Link to="/legal/shipping-returns" className="underline hover:text-[hsl(var(--accent))]">Shipping &amp; Returns Policy</Link> for timelines, the {POLICY_RETURN_DAYS()}-day return window and refund methods. It forms part of these Terms.</> },
      { h: "6. Acceptable use", p: [
        "You agree not to misuse the platform — including fraud, infringing others' rights, uploading unlawful content, attempting to disrupt the service, or reselling inspected goods as new without disclosure.",
        "Vendors additionally agree to list only genuine, lawfully-sourced products and to honour orders and applicable warranties.",
      ]},
      { h: "7. Intellectual property", p: `The ${COMPANY.brand} name, logo, site design and content are the property of ${COMPANY.legalName} and may not be used without written permission. Product images and brand marks belong to their respective owners.` },
      { h: "8. Limitation of liability", p: "To the extent permitted by law, the Company is not liable for indirect or consequential losses. Our aggregate liability for any claim is limited to the value of the order giving rise to the claim." },
      { h: "9. Governing law & jurisdiction", p: "These Terms are governed by the laws of India. The courts at New Delhi shall have exclusive jurisdiction, subject to applicable consumer-protection law that allows a consumer to approach their local forum." },
      { h: "10. Grievances", p: <>For any complaint, contact our Grievance Officer per the <Link to="/legal/grievance" className="underline hover:text-[hsl(var(--accent))]">Grievance Redressal</Link> page.</> },
    ],
  },

  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy.",
    intro: `This policy explains how ${COMPANY.legalName} collects, uses and protects your personal data when you use ${COMPANY.brand}. We follow the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023.`,
    sections: [
      { h: "1. Information we collect", p: [
        "Account & contact: name, email address, mobile number (verified by OTP), and password (stored only as a secure hash).",
        "Order & delivery: shipping address, order history, and communications with support.",
        "Payment: handled entirely by Razorpay. We receive only a payment reference and status — never your full card, UPI or bank details.",
        "Technical: basic device/usage data needed to operate and secure the site.",
      ]},
      { h: "2. How we use it", p: [
        "To process orders, arrange delivery, verify identity at checkout, issue refunds, and provide support.",
        "To send transactional messages (order confirmations, password resets, delivery and grievance updates) by email and SMS.",
        "To detect and prevent fraud, and to comply with legal and tax obligations.",
      ]},
      { h: "3. Who we share it with", p: [
        "Vendors — the seller of your item receives the details needed to fulfil and ship your order.",
        "Logistics partners (e.g. Shiprocket and couriers) — to deliver your order and process returns.",
        "Payment partner (Razorpay) — to process payments and refunds.",
        "Communication partners (e.g. Brevo for email, MSG91 for SMS) — to send transactional messages.",
        "Authorities — where required by law. We do not sell your personal data.",
      ]},
      { h: "4. Cookies", p: "We use only essential cookies/'local storage' needed to keep you signed in and to remember your cart and preferences. We do not run third-party advertising trackers." },
      { h: "5. Data retention & security", p: "We keep personal data only as long as needed for the purposes above or as required by law (e.g. tax records). Passwords are hashed and transport is encrypted (HTTPS)." },
      { h: "6. Your rights", p: <>You may access, correct or request deletion of your personal data, and withdraw consent for non-essential processing, by writing to <a href={`mailto:${COMPANY.email}`} className="underline hover:text-[hsl(var(--accent))]">{COMPANY.email}</a>. Certain records may be retained where the law requires.</> },
      { h: "7. Grievances", p: <>Questions or complaints about your data? Contact our Grievance Officer via the <Link to="/legal/grievance" className="underline hover:text-[hsl(var(--accent))]">Grievance Redressal</Link> page.</> },
    ],
  },

  "shipping-returns": {
    eyebrow: "Policy",
    title: "Shipping & Returns.",
    intro: "How your order reaches you, and how returns and refunds work.",
    sections: [
      { h: "Shipping", p: [
        "Vendors ship to the Spotters Hub, where every item is physically inspected before it goes out for delivery to you.",
        "Free shipping applies on orders of ₹2,000 and above; a flat ₹99 applies below that.",
        "Estimated delivery is typically 3–8 working days depending on your location and the item. Tracking is available under My Orders once dispatched.",
      ]},
      { h: "Returns", p: [
        `You can request a return within ${POLICY_RETURN_DAYS()} days of delivery from My Orders → Request Return, provided the item is unused, unworn and in its original condition and packaging.`,
        "Certain items may be non-returnable for hygiene or made-to-order reasons; this is noted on the product page where applicable.",
        "Once the Vendor confirms receipt of the returned item, your refund is processed.",
      ]},
      { h: "Refunds", p: [
        "You choose at return time: back to your original payment method (typically 5–7 working days via Razorpay), or as store credit that applies instantly to future orders.",
        "Shipping charges (if any) are non-refundable unless the return is due to a defective, damaged or incorrect item.",
      ]},
      { h: "Cancellations", p: "You can cancel an order before it is dispatched for a full refund. Once dispatched, the return process applies." },
      { h: "Questions", p: <>See <Link to="/help" className="underline hover:text-[hsl(var(--accent))]">Help &amp; Support</Link> or contact <a href={`mailto:${COMPANY.email}`} className="underline hover:text-[hsl(var(--accent))]">{COMPANY.email}</a>.</> },
    ],
  },

  grievance: {
    eyebrow: "Compliance",
    title: "Grievance Redressal.",
    intro: `In accordance with the Consumer Protection (E-Commerce) Rules, 2020 and the Information Technology (Intermediary Guidelines) Rules, 2021, the details of the operating entity and the Grievance Officer are published below.`,
    sections: [
      { h: "Operating entity", p: IDENTITY },
      { h: "Grievance Officer", p: (
        <div className="not-prose border border-foreground p-6 font-mono text-[13px] leading-relaxed tag-punch pl-10">
          <p><span className="text-muted-foreground">Name:</span> {go.name}</p>
          <p><span className="text-muted-foreground">Email:</span> <a href={`mailto:${go.email}`} className="underline hover:text-[hsl(var(--accent))]">{go.email}</a></p>
          <p><span className="text-muted-foreground">Phone:</span> {go.phone}</p>
          <p><span className="text-muted-foreground">Address:</span> {addr}</p>
          <p className="text-muted-foreground mt-2">Hours: Mon–Sat, 10:00–18:00 IST</p>
        </div>
      )},
      { h: "How to raise a complaint", p: [
        `Write to the Grievance Officer at ${go.email} with your order ID and a description of the issue.`,
        "We will acknowledge your complaint within 48 hours of receipt.",
        "We will endeavour to resolve it within 30 days from the date of receipt, in line with applicable law.",
      ]},
      { h: "Consumer redressal", p: "Nothing here limits your rights under the Consumer Protection Act, 2019. You may also approach the National Consumer Helpline (1915) or the appropriate Consumer Disputes Redressal Commission." },
    ],
  },
};

function POLICY_RETURN_DAYS() { return 7; }

function Section({ h, p }) {
  const body = Array.isArray(p)
    ? p.map((para, i) => <p key={i} className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{para}</p>)
    : typeof p === "string"
      ? <p className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{p}</p>
      : <div className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{p}</div>;
  return (
    <section className="py-6">
      <h2 className="font-serif text-2xl tracking-tight">{h}</h2>
      {body}
    </section>
  );
}

const NAV = [
  { slug: "terms", label: "Terms of Use" },
  { slug: "privacy", label: "Privacy" },
  { slug: "shipping-returns", label: "Shipping & Returns" },
  { slug: "grievance", label: "Grievance Redressal" },
];

export default function Legal() {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!policy) return <NotFound />;

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{policy.eyebrow}</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter">{policy.title}</h1>
      <p className="mt-5 text-muted-foreground leading-relaxed max-w-2xl">{policy.intro}</p>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Last updated · {UPDATED}</p>

      <nav className="mt-8 flex flex-wrap gap-x-5 gap-y-2 border-y border-foreground/15 py-4">
        {NAV.map((n) => (
          <Link key={n.slug} to={`/legal/${n.slug}`}
            className={`font-mono text-[11px] uppercase tracking-widest hover:text-[hsl(var(--accent))] ${n.slug === slug ? "text-foreground" : "text-muted-foreground"}`}>
            {n.label}
          </Link>
        ))}
      </nav>

      <div className="mt-4 divide-y divide-foreground/12">
        {policy.sections.map((s, i) => <Section key={i} {...s} />)}
      </div>
    </div>
  );
}
